import { ExpensePageFinanceCustomCategoryFragment } from 'generated/resources/graphql'

import {
  Currency,
  ExpensePageProject,
  ExpensePageSubproject,
} from 'modules/expenses/hooks/useExpensesListPage'
import { type Section } from 'modules/expenses/lib/RouteContextParams'
import { isMyExpensesSection } from 'modules/expenses/lib/common'

import approvedAtFilter from './ApprovedAtFilter'
import categoryFilter from './CategoryFilter'
import currencyFilter from './CurrencyFilter'
import documentTypeFilter from './DocumentTypeFilter'
import expenseTypeFilter from './ExpenseTypeFilter'
import imputableProjectFilter from './ImputableProjectFilter'
import internalReferenceFilter from './InternalReferenceFilter'
import legalEntityFilter from './LegalEntityFilter'
import ownerFilter from './OwnerFilter'
import pendingApprovalFilter from './PendingApprovalFilter'
import reimbursableCurrencyFilter from './ReimbursableCurrencyFilter'
import reimbursableFilter from './ReimbursableFilter'
import reportDateFilter from './ReportDateFilter'
import reportedByFilter from './ReportedByFilter'
import spendingAlertFilter from './SpendingAlertFilter'
import statusFilter from './StatusFilter'
import subcategoryFilter from './SubcategoryFilter'
import subprojectFilter from './SubprojectFilter'
import { InitialFilterValue } from './types'

interface Props {
  categories: ExpensePageFinanceCustomCategoryFragment[]
  currencies: Currency[]
  projects?: ExpensePageProject[]
  subprojects?: ExpensePageSubproject[]
  subcategories: ExpensePageFinanceCustomCategoryFragment[]
  expensesContext: Section
  withinGroup: boolean
  initialFilterValues: InitialFilterValue
  clearQuickFilters?: () => void
  includeReportedByFilter: boolean
  canCreateExpensesForOthers: boolean
  employeeId?: string
  recommendationsEnabled?: boolean
}

export const allFilters = ({
  categories,
  currencies,
  subprojects,
  subcategories,
  expensesContext,
  withinGroup,
  initialFilterValues,
  clearQuickFilters,
  includeReportedByFilter,
  canCreateExpensesForOthers,
  employeeId,
  recommendationsEnabled,
}: Props) => {
  const baseProps = { expensesContext, initialFilterValues }

  const filters = [
    statusFilter({ ...baseProps, clearQuickFilters }),
    expenseTypeFilter(baseProps),
    documentTypeFilter(baseProps),
    reimbursableFilter(baseProps),
    reportDateFilter({ ...baseProps }),
    categoryFilter({ ...baseProps, categories }),
    currencyFilter({ ...baseProps, currencies }),
    reimbursableCurrencyFilter({ ...baseProps, currencies }),
    subcategoryFilter({ ...baseProps, subcategories }),
    internalReferenceFilter({ ...baseProps }),
    spendingAlertFilter({
      ...baseProps,
      visible: true,
      recommendationsEnabled,
    }),
    approvedAtFilter({ ...baseProps }),
  ]

  if (isMyExpensesSection(expensesContext)) {
    if (includeReportedByFilter) filters.push(reportedByFilter(baseProps))
    if (includeReportedByFilter && canCreateExpensesForOthers && !withinGroup) {
      filters.push(ownerFilter({ ...baseProps, visible: true }))
    }
    if (employeeId) filters.push(imputableProjectFilter({ ...baseProps, employeeId }))
  } else {
    if (!withinGroup) filters.push(ownerFilter({ ...baseProps }))
    filters.push(legalEntityFilter({ ...baseProps }))
    filters.push(pendingApprovalFilter(baseProps))
    filters.push(imputableProjectFilter({ ...baseProps }))
    if (subprojects?.length) filters.push(subprojectFilter({ ...baseProps, subprojects }))
  }

  return filters
}
